/**
 * The sheet registry: what each record type opens. Each screen that moves to
 * the sheet pattern registers its body here (lazily imported). A type with no
 * body yet opens the placeholder sheet, which links to the record's full page.
 */
import {
  ArrowDataTransferHorizontalIcon,
  Briefcase01Icon,
  CheckListIcon,
  IdVerifiedIcon,
  PercentIcon,
  Shield01Icon,
  SlidersHorizontalIcon,
  Ticket01Icon,
  UserIcon,
  UserSearch01Icon,
} from '@hugeicons/core-free-icons';
import { type ComponentType, type LazyExoticComponent, lazy } from 'react';
import type { IconSvgObject } from '@/lib/icon-types';
import type { SheetRef, SheetType, SheetWidth } from '@/lib/sheet-url';

/** Props of every sheet body. */
export interface SheetBodyProps {
  id: string;
}

type SheetBody = ComponentType<SheetBodyProps>;

export interface SheetRegistryEntry {
  type: SheetType;
  /** What one record is called: "Payment", "Customer". The title before data loads. */
  noun: string;
  icon: IconSvgObject;
  /** Route path of the record's full page, when the type has one. */
  fullPage?: (id: string) => string;
  defaultWidth: SheetWidth;
  /** Lazily imports the body; leave out until the screen has one. */
  load?: () => Promise<{ default: SheetBody }>;
}

export const SHEET_REGISTRY: Record<SheetType, SheetRegistryEntry> = {
  cust: {
    type: 'cust',
    noun: 'Customer',
    icon: UserSearch01Icon,
    fullPage: (id) => `/customers/${encodeURIComponent(id)}`,
    defaultWidth: 'wide',
    load: () => import('@/components/customers/customer-sheet'),
  },
  tx: {
    type: 'tx',
    noun: 'Transaction',
    icon: ArrowDataTransferHorizontalIcon,
    fullPage: (id) => `/transactions/${encodeURIComponent(id)}`,
    defaultWidth: 'standard',
    load: () => import('@/components/transactions/transaction-sheet'),
  },
  issue: {
    type: 'issue',
    noun: 'Issue report',
    icon: Ticket01Icon,
    fullPage: (id) => `/issues/${encodeURIComponent(id)}`,
    defaultWidth: 'standard',
    load: () => import('@/components/issues/issue-sheet'),
  },
  case: {
    type: 'case',
    noun: 'Case',
    icon: Briefcase01Icon,
    fullPage: (id) => `/cases/${encodeURIComponent(id)}`,
    defaultWidth: 'wide',
    load: () => import('@/components/case/case-sheet'),
  },
  change: {
    type: 'change',
    noun: 'Change request',
    icon: CheckListIcon,
    fullPage: (id) => `/approvals/${encodeURIComponent(id)}`,
    defaultWidth: 'wide',
    load: () => import('@/components/approvals/change-sheet'),
  },
  kyc: {
    type: 'kyc',
    noun: 'ID check',
    icon: IdVerifiedIcon,
    defaultWidth: 'wide',
    load: () => import('@/components/verifications/kyc-sheet'),
  },
  money: {
    type: 'money',
    noun: 'Money record',
    icon: ArrowDataTransferHorizontalIcon,
    defaultWidth: 'standard',
    load: () => import('@/components/money/money-sheet'),
  },
  referral: {
    type: 'referral',
    noun: 'Referral',
    icon: UserSearch01Icon,
    defaultWidth: 'standard',
    load: () => import('@/components/referrals/referral-sheet'),
  },
  audit: {
    type: 'audit',
    noun: 'Audit entry',
    icon: Shield01Icon,
    defaultWidth: 'standard',
    load: () => import('@/components/audit/audit-sheet'),
  },
  staff: {
    type: 'staff',
    noun: 'Staff member',
    icon: UserIcon,
    fullPage: (id) => `/staff/${encodeURIComponent(id)}`,
    defaultWidth: 'standard',
    load: () => import('@/components/staff/staff-sheet'),
  },
  fee: {
    type: 'fee',
    noun: 'Tariff',
    icon: PercentIcon,
    defaultWidth: 'wide',
    load: () => import('@/components/fees/tariff-sheet'),
  },
  limit: {
    type: 'limit',
    noun: 'Limit group',
    icon: SlidersHorizontalIcon,
    defaultWidth: 'wide',
    load: () => import('@/components/limits/limit-sheet'),
  },
};

const bodies = new Map<SheetType, LazyExoticComponent<SheetBody>>();

/**
 * The lazy body for a type, created once so a re-render of the host never
 * remounts an open sheet. Null while the type has no body yet.
 */
export function sheetBody(
  type: SheetType,
): LazyExoticComponent<SheetBody> | null {
  const load = SHEET_REGISTRY[type].load;
  if (!load) return null;
  let body = bodies.get(type);
  if (!body) {
    body = lazy(load);
    bodies.set(type, body);
  }
  return body;
}

/** Route path of a record's full page, or undefined when the type has none. */
export function fullPagePath(ref: SheetRef): string | undefined {
  return SHEET_REGISTRY[ref.type].fullPage?.(ref.id);
}
